import { useState, useCallback, useEffect } from 'react';
import { Box, Text } from '@/components/primitives';
import { Input } from '@/components/primitives';
import { useMutation } from '@/api/mutation';
import { createOrganization } from '@/api/staff';
import type { Organization } from '@/api/staff';
import { StyledDialog, StyledDialogFooterActions } from '@/components/dialogs';
import { Icons } from '@/theme';
import { testIds } from '@/test/testIds';
import { useFeedbackZone } from '@/components/ui/FeedbackZone';
import { reportMutationError } from '@/api/reportMutationError';
import { dialogMessages } from '@/constants';

export interface CreateOrganizationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (organization: Organization) => void;
}

/**
 * CreateOrganizationDialog - Create a new organization
 *
 * Feedback ownership (#821): a dialog component's own `useFeedbackZone()`
 * resolves to its PARENT layer, so this component holds only `pageZone` (the
 * "created" success outlives the dialog) and `CreateOrganizationActions` — the
 * footer, rendered inside the StyledDialog — owns the create: its failures
 * land in this dialog's own band.
 */
export function CreateOrganizationDialog({
  open,
  onClose,
  onSuccess,
}: CreateOrganizationDialogProps) {
  // Resolved OUTSIDE the StyledDialog → the page zone, which survives close.
  const pageZone = useFeedbackZone();
  // Lifted from the footer: a create in flight blocks closing.
  const [creating, setCreating] = useState(false);
  // Form state initializes once per mount. Consumers bump a session key
  // each time they open this dialog so the component remounts with empty
  // fields; no in-component reset Effect.
  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [identifierTouched, setIdentifierTouched] = useState(false);

  // Auto-generate identifier from name (if not manually touched)
  const generateIdentifier = useCallback((nameValue: string) => {
    // Strip trailing hyphens AFTER substring as well — the 12-char cap can
    // land mid-hyphen (e.g., "a b c d e f g" → "a-b-c-d-e-f-").
    return nameValue
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 12)
      .replace(/-$/g, '');
  }, []);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!identifierTouched) {
      setIdentifier(generateIdentifier(value));
    }
  };

  const handleIdentifierChange = (value: string) => {
    setIdentifierTouched(true);
    setIdentifier(value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  };

  const handleCreated = (organization: Organization) => {
    pageZone.push(dialogMessages.staffManagement.success.organizationCreated, 'success');
    onSuccess?.(organization);
    onClose();
  };

  const isValid = name.trim().length > 0 && identifier.trim().length >= 3;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      title={dialogMessages.staffManagement.createOrganization.title}
      titleIcon={<Icons.organization />}
      closeDisabled={creating}
      footer={
        <CreateOrganizationActions
          input={isValid ? { name, identifier } : null}
          onCreated={handleCreated}
          onCancel={onClose}
          onBusyChange={setCreating}
        />
      }
      testId={testIds.staffManagement.createOrgDialog}
    >
      <Box display="flex" flexDirection="column" gap="md" style={{ minWidth: 'min(400px, 100%)' }}>
        <Input
          label="Name"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          autoFocus
          required
          data-testid={testIds.staffManagement.nameInput}
        />
        <Box>
          <Input
            label="Identifier"
            value={identifier}
            onChange={(e) => handleIdentifierChange(e.target.value)}
            required
            inputProps={{ maxLength: 12 }}
            data-testid={testIds.staffManagement.identifierInput}
          />
          <Text variant="caption" color="secondary" style={{ marginTop: 0.5, display: 'block' }}>
            {dialogMessages.staffManagement.createOrganization.identifierHelp}
          </Text>
        </Box>
      </Box>
    </StyledDialog>
  );
}

/**
 * The dialog's footer, rendered INSIDE the StyledDialog, so the create's
 * failures reach this dialog's own band through `useFeedbackZone`.
 */
function CreateOrganizationActions({
  input,
  onCreated,
  onCancel,
  onBusyChange,
}: {
  /** The organization to create, or null while the form is incomplete. */
  input: { name: string; identifier: string } | null;
  /** Success: the caller reports it on a zone that survives close, then closes. */
  onCreated: (organization: Organization) => void;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const zone = useFeedbackZone();
  const mutation = useMutation({
    mutationFn: (body: { name: string; identifier: string }) => createOrganization(body),
    onSuccess: onCreated,
    onError: (error) => {
      // 5xx + transport errors are surfaced centrally by routeMutationError,
      // in this dialog's band (#1174).
      reportMutationError(zone, error, { message: dialogMessages.staffManagement.error.createFailed });
    },
  });

  const isPending = mutation.isPending;
  useEffect(() => {
    onBusyChange(isPending);
    // A forced unmount mid-create must not leave the dialog locked.
    return () => onBusyChange(false);
  }, [isPending, onBusyChange]);

  return (
    <StyledDialogFooterActions
      footer={{
        variant: 'confirm',
        primaryText: 'Create',
        onPrimary: () => {
          if (input) mutation.mutate(input);
        },
        onSecondary: onCancel,
        loading: isPending,
        loadingText: 'Creating...',
        primaryDisabled: input === null,
      }}
      onClose={onCancel}
    />
  );
}
